import { DeliveriesSummary } from "./DeliveriesSummary";
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Check, Loader2, AlertTriangle, ShieldCheck, Package, Truck, CreditCard, Plus, Minus, Trash2, Search, Pencil, Copy as DuplicateIcon, Calendar as CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import Header from "./Header";
import Footer from "./Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { api, formatApiErrorDetail } from "../lib/api";
import { US_STATES, ZIP_RE } from "../lib/usStates";

// Public-facing invoice viewer + pay-now flow. Accessed via
// /invoice/{share_token} — the token comes from the email link the admin
// sent. The customer completes any missing contact/address info, clicks
// Pay Now, and is redirected to Stripe. On successful payment we auto-
// create an approved agency account for their email (see server.py
// stripe_webhook for the account-creation logic).
export default function PublicInvoicePage() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [menuById, setMenuById] = useState({});
  // Editable local copy of the line items — the customer can add / remove,
  // change qty, and swap modifiers. Sent to the server on Pay.
  const [items, setItems] = useState([]);
  const [addQuery, setAddQuery] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    organization: "",
    address: "",
    address_line2: "",
    city: "",
    state: "NY",
    zip_code: "",
    event_date: "",
    event_time: "",
    delivery_instructions: "",
    on_site_contact_name: "",
    on_site_contact_phone: "",
    has_receiving_personnel: false,
    has_delivery_cart: false,
  });

  const paidQuery = search.get("paid") === "1";
  const wasPaid = paidQuery || invoice?.status === "paid";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch invoice + full menu in parallel. Menu is public and returns
        // retail prices for unauthenticated calls — perfect for the
        // editable-line-items UI on this page.
        const [invRes, menuRes] = await Promise.all([
          api.get(`/public/invoices/${token}`),
          api.get(`/menu`),
        ]);
        if (cancelled) return;
        const inv = invRes.data;
        const rawMenu = menuRes.data?.items || [];
        const byId = Object.fromEntries(rawMenu.map((m) => [m.id, m]));
        setInvoice(inv);
        setMenuItems(rawMenu);
        setMenuById(byId);
        setItems(
          (inv.items || []).map((it) => {
            // Durable price-override honor. When an admin authored this
            // line with a custom price (open item, negotiated rate, or a
            // named package priced off-catalog), the server stamps
            // `price_override: true` + `unit_price: <n>` into the stored
            // snapshot. Preserve BOTH fields on the client and lock the
            // unit_price to the stored value. Only fall back to the live
            // catalog price when there is NO override — that keeps the
            // "menu price change auto-propagates" behaviour for normal
            // retail lines while stopping the silent price drift Philip
            // caught on INV-2026-000036 (admin-priced $45 slider was
            // being rehydrated as $40 catalog + no cheese modifier).
            const hasOverride = Boolean(
              it.price_override && it.unit_price != null,
            );
            return {
              package_id: it.package_id,
              // Prefer the server-normalised line name (already includes
              // any custom line_label from the admin), then fall back to
              // the catalog name only if the snapshot is missing one.
              name: it.name || byId[it.package_id]?.name || "",
              quantity: Number(it.quantity) || 1,
              selected_options: it.selected_options || null,
              delivery_index: it.delivery_index,
              price_override: hasOverride,
              unit_price: hasOverride
                ? Number(it.unit_price)
                : Number((byId[it.package_id] && !byId[it.package_id].quote_only ? byId[it.package_id].price : it.unit_price) ?? 0),
            };
          }),
        );
        setForm((s) => ({
          ...s,
          customer_name: inv.customer_name || "",
          customer_email: inv.customer_email || "",
          customer_phone: inv.customer_phone || "",
          organization: inv.organization || "",
          address: inv.address || "",
          address_line2: inv.address_line2 || "",
          city: inv.city || "",
          state: inv.state || s.state,
          zip_code: inv.zip_code || "",
          event_date: inv.event_date || "",
          event_time: inv.event_time || "",
          delivery_instructions: inv.delivery_instructions || "",
          on_site_contact_name: inv.on_site_contact_name || "",
          on_site_contact_phone: inv.on_site_contact_phone || "",
        }));
      } catch (e) {
        setError(
          formatApiErrorDetail(e?.response?.data?.detail) ||
            (e?.response?.status === 404
              ? "This invoice link isn't valid or has expired."
              : "Could not load this invoice.")
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const isDelivery = invoice?.fulfillment === "delivery";
  const isMultiDrop = (invoice?.deliveries?.length || 0) > 1;
  const missingFields = useMemo(() => {
    const req = ["customer_name", "customer_email", "customer_phone"];
    if (!isMultiDrop) req.push("event_date", "event_time");
    if (isDelivery && !isMultiDrop) req.push("address", "city", "state", "zip_code");
    return req.filter((k) => !(form[k] || "").trim());
  }, [form, isDelivery, isMultiDrop]);

  // ── Line-item editing helpers ──────────────────────────────────────────
  // Live totals so the customer sees the price change as they edit. The
  // authoritative totals still come from the server on Pay — this is
  // preview only.
  const optionUnitDelta = (pkg, selectedOptions) => {
    let delta = 0;
    for (const grp of pkg?.options || []) {
      const picked = selectedOptions?.[grp.label];
      const pickedList = Array.isArray(picked) ? picked : (picked ? [picked] : []);
      for (const v of grp.values || []) {
        const label = typeof v === "string" ? v : v.label;
        if (pickedList.includes(label)) {
          delta += typeof v === "string" ? 0 : (Number(v.price_delta) || 0);
        }
      }
    }
    return delta;
  };
  const lineUnitPrice = (line) => {
    // Durable price-override honor. When the admin creates a custom-priced
    // line (open-item burger, negotiated per-guest rate, delivery fee),
    // `_compute_totals` on the backend stamps the line with
    // `price_override: true` + `unit_price: <n>` in the stored order
    // snapshot. That override MUST win over the catalog + modifier-delta
    // computation, otherwise re-opening the invoice on this page silently
    // "recomputes" the price back to catalog and the subtotal drifts —
    // exactly the bug Philip caught on INV-2026-000036 where a $45
    // slider override displayed as $40 ea and the total showed $1,315
    // instead of $1,450.
    if (line.price_override && line.unit_price != null) {
      return Number(line.unit_price) || 0;
    }
    // Some code paths (admin PATCH replay) store the override under
    // `unit_price_override` without the `price_override` marker — accept
    // that shape too, same durability rule.
    if (line.unit_price_override != null) {
      return Number(line.unit_price_override) || 0;
    }
    const pkg = menuById[line.package_id];
    if (!pkg || pkg.quote_only) return Number(line.unit_price) || 0;
    return Number(pkg.price || 0) + optionUnitDelta(pkg, line.selected_options);
  };
  const subtotalLive = useMemo(
    () => items.reduce((s, ln) => s + lineUnitPrice(ln) * ln.quantity, 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, menuById]
  );
  const deliveryLive = isDelivery ? Number(invoice?.delivery_fee || 0) : 0;
  const taxLive = invoice?.tax_exempt
    ? 0
    : Math.round(subtotalLive * (Number(invoice?.tax_rate) || 0.08875) * 100) / 100;
  const totalLive = subtotalLive + deliveryLive + taxLive;

  // Detect whether the customer has edited anything vs the server snapshot.
  const originalKey = useMemo(
    () => JSON.stringify(invoice?.items || []),
    [invoice]
  );
  const currentKey = useMemo(
    () => JSON.stringify(items.map((it) => ({ package_id: it.package_id, quantity: it.quantity, selected_options: it.selected_options || null }))),
    [items]
  );
  const itemsEdited = originalKey !== currentKey;

  const updateQty = (idx, next) => {
    const q = Math.max(1, Math.min(999, Number(next) || 1));
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, quantity: q } : it)));
  };
  const removeLine = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };
  const toggleOption = (idx, grpLabel, value, maxSelections = 1) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...(it.selected_options || {}) };
      const cur = Array.isArray(next[grpLabel]) ? next[grpLabel] : (next[grpLabel] ? [next[grpLabel]] : []);
      let updated;
      if (cur.includes(value)) {
        updated = cur.filter((v) => v !== value);
      } else if (maxSelections === 1) {
        updated = [value];
      } else {
        updated = [...cur, value].slice(-maxSelections);
      }
      if (updated.length === 0) {
        delete next[grpLabel];
      } else {
        next[grpLabel] = maxSelections === 1 ? updated[0] : updated;
      }
      return { ...it, selected_options: next };
    }));
  };
  const addLine = (pkg) => {
    setItems((prev) => [...prev, {
      package_id: pkg.id,
      name: pkg.name,
      quantity: 1,
      selected_options: null,
      unit_price: Number(pkg.price || 0),
    }]);
    setAddQuery("");
  };
  // "Build another with different modifiers" — clones a line but resets
  // its option selections so the customer can pick a different flavor/
  // sauce/config for the second pan.
  const duplicateLine = (idx) => {
    setItems((prev) => {
      const src = prev[idx];
      if (!src) return prev;
      const copy = { ...src, selected_options: null };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  };

  const filteredAddOptions = useMemo(() => {
    const q = addQuery.trim().toLowerCase();
    if (!q) return [];
    return menuItems
      .filter((m) => !m.quote_only && `${m.name} ${m.category || ""}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [addQuery, menuItems]);

  const zipValid = isMultiDrop || (form.zip_code ? ZIP_RE.test(form.zip_code) : !isDelivery);
  const emailValid = form.customer_email
    ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customer_email)
    : false;
  const canPay = missingFields.length === 0 && zipValid && emailValid && items.length > 0;

  const submitPayment = async () => {
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/invoices/${token}/checkout`, {
        ...form,
        // Only send items when the customer actually changed something —
        // this keeps admin-authored invoices canonical when untouched.
        ...(itemsEdited
          ? {
              items: items.map((it) => ({
                package_id: it.package_id,
                quantity: it.quantity,
                selected_options: it.selected_options || undefined,
              })),
            }
          : {}),
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError("Could not start payment. Please try again.");
      }
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e?.message ||
          "Payment could not be started."
      );
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-brand-cream">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-brand-teal" />
        </main>
        <Footer />
      </div>
    );
  }
  if (error && !invoice) {
    return (
      <div className="min-h-screen flex flex-col bg-brand-cream">
        <Header />
        <main className="flex-1 flex items-center justify-center p-6">
          <div
            data-testid="public-invoice-error"
            className="max-w-md rounded-3xl bg-white shadow-xl border border-red-200 p-8 text-center"
          >
            <div className="mx-auto h-14 w-14 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h1 className="mt-4 font-display font-black text-[22px] text-brand-charcoal">
              Invoice unavailable
            </h1>
            <p className="mt-2 text-[13.5px] text-brand-charcoal/65">{error}</p>
            <a
              href="/"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.12em]"
            >
              Return home
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (invoice?.status === "awaiting_po") {
    return (
      <div className="min-h-screen flex flex-col bg-brand-cream">
        <Header />
        <main className="flex-1 flex items-center justify-center p-6">
          <div data-testid="public-invoice-awaiting-po" className="max-w-md rounded-3xl bg-white shadow-xl border border-black/5 p-8 text-center">
            <p className="text-[10.5px] font-black uppercase tracking-[0.16em] text-brand-teal">
              Invoice {invoice.invoice_number} · Agency pricing
            </p>
            <h1 className="mt-2 font-display font-black text-[22px] text-brand-charcoal">
              Settled by Purchase Order
            </h1>
            <p className="mt-2 text-[13.5px] text-brand-charcoal/65">
              Total ${Number(invoice.total || 0).toFixed(2)} (tax-exempt). No card payment is needed. Once your
              team approves, log in and submit your PO from My Invoices.
            </p>
            <a href="/account/invoices" data-testid="public-invoice-go-account" className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors">
              Go to My Invoices
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-brand-cream" data-testid="public-invoice-page">
      <Header />
      <main className="flex-1 py-10 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 space-y-6">
          {wasPaid && (
            <div
              data-testid="public-invoice-paid-banner"
              className="rounded-3xl bg-emerald-50 border border-emerald-200 px-6 py-5 flex items-start gap-3"
            >
              <div className="h-10 w-10 shrink-0 rounded-full bg-emerald-600 flex items-center justify-center">
                <Check className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="font-display font-black text-[19px] text-emerald-800 tracking-tight">
                  Payment received — thank you!
                </h2>
                <p className="mt-1 text-[13px] text-emerald-800/85">
                  We&apos;ll send a receipt to <strong>{form.customer_email || invoice.customer_email}</strong> and
                  reach out with next steps.
                  {invoice.customer_email && (
                    <> An agency account for this email has been created — you&apos;ll receive login instructions shortly.</>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Invoice header */}
          <section className="rounded-3xl bg-white shadow-lg border border-black/5 overflow-hidden">
            <div className="bg-brand-charcoal text-white px-6 py-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-gold">
                    Invoice
                  </p>
                  <h1 className="mt-1 font-display font-black text-[26px] tracking-tight" data-testid="public-invoice-number">
                    {invoice.invoice_number || "—"}
                  </h1>
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-white/60">Total due</p>
                  <p
                    className="font-display font-black text-[30px] tracking-tight tabular-nums text-brand-teal"
                    data-testid="public-invoice-total"
                  >
                    ${totalLive.toFixed(2)}
                  </p>
                  {invoice.tax_exempt && (
                    <p className="mt-0.5 text-[11px] uppercase tracking-[0.12em] font-bold text-brand-gold">
                      Tax-exempt
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Customer-visible note authored by admin. Shows only when the
                admin explicitly wrote one — this is distinct from internal
                `notes`, which never leave the admin panel. */}
            {invoice.customer_note && (
              <div
                data-testid="public-invoice-customer-note"
                className="px-6 pt-5"
              >
                <div className="rounded-2xl bg-brand-cream/60 border border-brand-cream px-4 py-3">
                  <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-teal">
                    A note from Fresh Fast Foods
                  </p>
                  <p className="mt-1 text-[13px] text-brand-charcoal leading-snug whitespace-pre-wrap">
                    {invoice.customer_note}
                  </p>
                </div>
              </div>
            )}

            {/* Line items — editable. Customer can change qty, swap modifier
                options, remove lines, or add new items. On Pay, we send the
                edited list to the server which recomputes canonical totals
                and notifies the admin the invoice was modified. */}
            <div className="p-6 space-y-4">
              {isMultiDrop ? (
                <DeliveriesSummary deliveries={invoice.deliveries} items={items} />
              ) : (
              <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                  <Pencil className="h-3 w-3" />
                  Review & adjust — you can change quantities, swap options, or add more items
                </div>
                {itemsEdited && !wasPaid && (
                  <span
                    data-testid="public-invoice-edited-badge"
                    className="inline-flex items-center gap-1 rounded-full bg-brand-gold/15 text-brand-charcoal px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.1em]"
                  >
                    <Check className="h-3 w-3" />
                    Adjusted
                  </span>
                )}
              </div>
              <div className="rounded-2xl bg-brand-cream/30 divide-y divide-black/5">
                {items.length === 0 && (
                  <p className="px-4 py-6 text-center text-[13px] text-brand-charcoal/50">
                    Your invoice is empty — add at least one item below to pay.
                  </p>
                )}
                {items.map((line, idx) => {
                  const pkg = menuById[line.package_id];
                  const lineTotal = lineUnitPrice(line) * line.quantity;
                  // Prefer the server-stored line name (already carries
                  // the admin's custom line_label when present) — falling
                  // back to catalog name only if the snapshot is missing.
                  const displayName = line.name || pkg?.name || line.package_id;
                  // Suppress the modifier picker on admin-overridden lines.
                  // The admin already priced this line; forcing the
                  // customer to re-pick modifiers would silently blow away
                  // the override and drift the price.
                  const hasOverride = Boolean(
                    (line.price_override && line.unit_price != null) ||
                    line.unit_price_override != null,
                  );
                  return (
                    <div
                      key={`${line.package_id}-${idx}`}
                      data-testid={`public-invoice-line-${line.package_id}`}
                      className="px-3 sm:px-4 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[14px] text-brand-charcoal leading-snug">
                            {/* Prominent quantity prefix so the customer
                                immediately reads "25 × Dozen Sliders" not
                                a bare number in a spinner widget. Only
                                shown on admin-priced (override) lines
                                where the customer can't change qty
                                anyway — for retail cart lines the +/-
                                spinner still handles quantity display. */}
                            {hasOverride && line.quantity > 1 && (
                              <span
                                data-testid={`public-invoice-qty-prefix-${line.package_id}-${idx}`}
                                className="text-brand-charcoal tabular-nums mr-1.5"
                              >
                                {line.quantity} ×
                              </span>
                            )}
                            {displayName}
                          </p>
                          <p className="text-[11.5px] text-brand-charcoal/50 tabular-nums">
                            ${lineUnitPrice(line).toFixed(2)} ea
                          </p>
                          {/* Compact modifier summary — e.g. "Steakhouse Beef ·
                              Pepper Jack" — so the customer sees what's in
                              each line without opening the picker. No price
                              deltas here (kept for the picker itself) to keep
                              the line tight. */}
                          {line.selected_options && Object.keys(line.selected_options).length > 0 && (
                            <p
                              data-testid={`public-invoice-mods-${line.package_id}`}
                              className="mt-0.5 text-[11px] text-brand-teal/85 leading-snug"
                            >
                              {Object.entries(line.selected_options)
                                .map(([, v]) => (Array.isArray(v) ? v.join(", ") : v))
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          )}
                        </div>
                        {wasPaid ? (
                          <span className="text-[13px] font-bold tabular-nums">×{line.quantity}</span>
                        ) : hasOverride ? (
                          // Admin-priced draft line — the "25 × Dozen
                          // Sliders w/ Cheese" prefix in the name already
                          // shows quantity, so rendering nothing here
                          // keeps the row visually tidy (no duplicate
                          // "×25" on the right). Layout note: this branch
                          // returns null so the flex spacing collapses,
                          // pushing the $ line-total flush against the
                          // right edge — same as an unpriced row.
                          null
                        ) : (
                          <div className="flex items-center gap-1.5 rounded-full bg-white border border-black/10 px-1">
                            <button
                              type="button"
                              data-testid={`public-invoice-qty-minus-${line.package_id}`}
                              onClick={() => updateQty(idx, line.quantity - 1)}
                              disabled={line.quantity <= 1}
                              className="p-1.5 rounded-full text-brand-charcoal/60 hover:text-brand-charcoal hover:bg-brand-cream disabled:opacity-30 disabled:hover:bg-transparent"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <input
                              type="number"
                              data-testid={`public-invoice-qty-${line.package_id}`}
                              min={1}
                              max={999}
                              value={line.quantity}
                              onChange={(e) => updateQty(idx, e.target.value)}
                              className="w-10 h-6 text-center text-[13px] font-bold tabular-nums bg-transparent border-0 focus:outline-none"
                            />
                            <button
                              type="button"
                              data-testid={`public-invoice-qty-plus-${line.package_id}`}
                              onClick={() => updateQty(idx, line.quantity + 1)}
                              className="p-1.5 rounded-full text-brand-charcoal/60 hover:text-brand-charcoal hover:bg-brand-cream"
                              aria-label="Increase quantity"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        <p className="w-20 text-right font-bold text-[13.5px] tabular-nums text-brand-charcoal">
                          ${lineTotal.toFixed(2)}
                        </p>
                        {!wasPaid && !hasOverride && (
                          <>
                            {pkg?.options?.length > 0 && !hasOverride && (
                              <button
                                type="button"
                                data-testid={`public-invoice-duplicate-${line.package_id}`}
                                onClick={() => duplicateLine(idx)}
                                title="Build another with different modifiers"
                                aria-label="Duplicate line"
                                className="p-1.5 rounded-full text-brand-teal hover:bg-brand-teal/10"
                              >
                                <DuplicateIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button
                              type="button"
                              data-testid={`public-invoice-remove-${line.package_id}`}
                              onClick={() => removeLine(idx)}
                              aria-label="Remove line"
                              className="p-1.5 rounded-full text-brand-charcoal/40 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                      {/* Modifier groups — one row per option group. Only
                          shown when a menu entry actually has options AND
                          the invoice isn't paid yet AND the admin didn't
                          already price the line (customer picker would
                          otherwise blow away the price override). */}
                      {!wasPaid && !hasOverride && pkg?.options?.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {pkg.options.map((grp) => {
                            const picked = line.selected_options?.[grp.label];
                            const pickedList = Array.isArray(picked)
                              ? picked
                              : (picked ? [picked] : []);
                            const maxSel = Number(grp.max_selections || 1);
                            return (
                              <div key={grp.label}>
                                <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/50 mb-1">
                                  {grp.label}{grp.required && " *"}
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                  {(grp.values || []).map((v) => {
                                    const label = typeof v === "string" ? v : v.label;
                                    const delta = typeof v === "string" ? 0 : (Number(v.price_delta) || 0);
                                    const active = pickedList.includes(label);
                                    return (
                                      <button
                                        key={label}
                                        type="button"
                                        data-testid={`public-invoice-opt-${line.package_id}-${grp.label}-${label}`.replace(/\s+/g, "-").toLowerCase()}
                                        onClick={() => toggleOption(idx, grp.label, label, maxSel)}
                                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors border ${
                                          active
                                            ? "bg-brand-teal text-white border-brand-teal"
                                            : "bg-white text-brand-charcoal/70 border-black/10 hover:border-brand-teal/50"
                                        }`}
                                      >
                                        {label}
                                        {delta > 0 && (
                                          <span className={active ? "text-white/85" : "text-brand-gold"}>
                                            +${delta.toFixed(2)}
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {/* Read-only options display when paid */}
                      {wasPaid && line.selected_options && Object.keys(line.selected_options).length > 0 && (
                        <p className="mt-1 text-[11.5px] text-brand-charcoal/55">
                          {Object.entries(line.selected_options).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join(" · ")}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Add-item search — hidden after payment */}
              {!wasPaid && (
                <div className="relative">
                  <div className="flex items-center gap-2 rounded-2xl bg-white border border-dashed border-black/15 px-3 py-2.5">
                    <Search className="h-4 w-4 text-brand-charcoal/40 shrink-0" />
                    <input
                      type="text"
                      data-testid="public-invoice-add-search"
                      placeholder="Add another item — search the menu"
                      value={addQuery}
                      onChange={(e) => setAddQuery(e.target.value)}
                      className="flex-1 min-w-0 bg-transparent text-[13px] text-brand-charcoal placeholder-brand-charcoal/40 focus:outline-none"
                    />
                  </div>
                  {filteredAddOptions.length > 0 && (
                    <div className="absolute z-20 top-full mt-1 left-0 right-0 rounded-2xl bg-white shadow-lg border border-black/10 max-h-56 overflow-auto">
                      {filteredAddOptions.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          data-testid={`public-invoice-add-${m.id}`}
                          onClick={() => addLine(m)}
                          className="w-full text-left px-4 py-2.5 hover:bg-brand-cream/60 flex items-center gap-3"
                        >
                          <Plus className="h-3.5 w-3.5 text-brand-teal shrink-0" />
                          <span className="flex-1 min-w-0 truncate text-[13px] text-brand-charcoal">{m.name}</span>
                          <span className="text-[11.5px] text-brand-charcoal/50 truncate">{m.category}</span>
                          <span className="text-[12px] font-bold text-brand-charcoal/70 tabular-nums shrink-0">${Number(m.price || 0).toFixed(2)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              </>
              )}

              {/* Totals — computed live from the current items */}
              <div className="rounded-2xl bg-white border border-black/5 px-4 py-3 space-y-1.5">
                <div className="flex justify-between text-[13px] text-brand-charcoal/70">
                  <span>Subtotal</span>
                  <span className="tabular-nums" data-testid="public-invoice-subtotal">${subtotalLive.toFixed(2)}</span>
                </div>
                {isDelivery && (
                  <div className="flex justify-between text-[13px] text-brand-charcoal/70">
                    <span>Delivery</span>
                    <span className="tabular-nums">${deliveryLive.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[13px] text-brand-charcoal/70">
                  <span>
                    Tax {invoice.tax_exempt && <span className="ml-1 text-[10px] uppercase tracking-[0.1em] font-bold text-brand-gold">exempt</span>}
                  </span>
                  <span className="tabular-nums">${taxLive.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-black text-[15px] text-brand-charcoal pt-1.5 border-t border-black/10 mt-1">
                  <span>Total</span>
                  <span className="tabular-nums" data-testid="public-invoice-live-total">${totalLive.toFixed(2)}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-[11.5px] text-brand-charcoal/50">
                <ShieldCheck className="h-3.5 w-3.5" />
                Payment processed securely by Stripe. We never store your card.
              </div>
            </div>
          </section>

          {/* Contact & delivery form + Pay button */}
          {!wasPaid && (
            <section className="rounded-3xl bg-white shadow-lg border border-black/5 overflow-hidden">
              <div className="px-6 py-5 border-b border-black/5">
                <h2 className="font-display font-black text-[19px] text-brand-charcoal tracking-tight">
                  Confirm your details
                </h2>
                <p className="mt-1 text-[12.5px] text-brand-charcoal/60">
                  These flow onto your receipt and let us reach you for delivery coordination.
                </p>
              </div>
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    label="Your name *"
                    testid="pi-name"
                    value={form.customer_name}
                    onChange={(v) => setForm({ ...form, customer_name: v })}
                  />
                  <FormField
                    label="Organization"
                    testid="pi-org"
                    value={form.organization}
                    onChange={(v) => setForm({ ...form, organization: v })}
                  />
                  <FormField
                    label="Email *"
                    testid="pi-email"
                    type="email"
                    value={form.customer_email}
                    onChange={(v) => setForm({ ...form, customer_email: v })}
                  />
                  <FormField
                    label="Phone *"
                    testid="pi-phone"
                    value={form.customer_phone}
                    onChange={(v) => setForm({ ...form, customer_phone: v })}
                  />
                </div>

                {!isMultiDrop && (
                <>
                {/* Event date + time — the customer must confirm when they
                    want the food. Admin may have pre-filled these; the
                    customer can still change them here. */}
                <div>
                  <div className="flex items-center gap-2 text-[11.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 mb-2">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {isDelivery ? "Delivery date & time" : "Pickup date & time"}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                        {isDelivery ? "Drop-off date *" : "Pickup date *"}
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid="pi-event-date"
                            className="mt-1.5 flex h-11 w-full items-center justify-between rounded-xl border border-black/10 bg-brand-cream/40 px-3 text-left text-[14px] hover:bg-brand-cream/70 focus:outline-none focus:ring-2 focus:ring-brand-teal"
                          >
                            <span className={form.event_date ? "text-brand-charcoal" : "text-brand-charcoal/40"}>
                              {form.event_date
                                ? format(parseISO(form.event_date), "EEE, MMM d, yyyy")
                                : "Pick a date"}
                            </span>
                            <CalendarIcon className="h-4 w-4 text-brand-charcoal/50 shrink-0" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={form.event_date ? parseISO(form.event_date) : undefined}
                            onSelect={(d) =>
                              setForm((s) => ({ ...s, event_date: d ? format(d, "yyyy-MM-dd") : "" }))
                            }
                            disabled={(d) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              return d < today;
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                        {isDelivery ? "Drop-off time *" : "Pickup time *"}
                      </Label>
                      <Input
                        data-testid="pi-event-time"
                        type="time"
                        value={form.event_time}
                        onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                        className="mt-1.5 h-11 rounded-xl bg-brand-cream/40 border-black/10"
                      />
                    </div>
                  </div>
                </div>

                {isDelivery ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-[11.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                      <Truck className="h-3.5 w-3.5" />
                      Delivery address
                    </div>
                    <FormField
                      label="Street address *"
                      testid="pi-addr"
                      value={form.address}
                      onChange={(v) => setForm({ ...form, address: v })}
                    />
                    <FormField
                      label="Suite / Unit"
                      testid="pi-addr2"
                      value={form.address_line2}
                      onChange={(v) => setForm({ ...form, address_line2: v })}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormField
                        label="City *"
                        testid="pi-city"
                        value={form.city}
                        onChange={(v) => setForm({ ...form, city: v })}
                      />
                      <div>
                        <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                          State *
                        </Label>
                        <select
                          data-testid="pi-state"
                          value={form.state}
                          onChange={(e) => setForm({ ...form, state: e.target.value })}
                          className="mt-1.5 w-full h-11 rounded-xl bg-brand-cream/40 border border-black/10 px-3 text-[13px] text-brand-charcoal"
                        >
                          {US_STATES.map(([code, name]) => (
                            <option key={code} value={code}>{code} — {name}</option>
                          ))}
                        </select>
                      </div>
                      <FormField
                        label="ZIP *"
                        testid="pi-zip"
                        value={form.zip_code}
                        onChange={(v) => setForm({ ...form, zip_code: v })}
                        error={form.zip_code && !zipValid ? "5-digit ZIP" : ""}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                        Delivery instructions (optional)
                      </Label>
                      <Textarea
                        data-testid="pi-instructions"
                        value={form.delivery_instructions}
                        onChange={(e) => setForm({ ...form, delivery_instructions: e.target.value })}
                        className="mt-1.5 rounded-xl bg-brand-cream/40 border-black/10"
                        rows={2}
                        placeholder="Loading dock, freight elevator, contact on arrival, etc."
                      />
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl bg-brand-cream/30 border border-black/5 px-4 py-3 flex items-center gap-2 text-[12.5px] text-brand-charcoal/70">
                    <Package className="h-4 w-4 text-brand-teal" />
                    Pickup order — we&apos;ll coordinate the pickup window after payment.
                  </div>
                )}

                </>
                )}

                {error && (
                  <div
                    data-testid="public-invoice-form-error"
                    role="alert"
                    className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="button"
                  data-testid="public-invoice-pay-btn"
                  onClick={submitPayment}
                  disabled={!canPay || submitting}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-4 text-[13px] font-bold uppercase tracking-[0.14em] hover:bg-brand-teal transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  Pay ${totalLive.toFixed(2)} securely
                </button>
                {!canPay && !error && (
                  <p className="text-[11.5px] text-brand-charcoal/50 text-center">
                    {missingFields.length > 0
                      ? `Please fill in the required fields (${missingFields.length} remaining).`
                      : "Please check that all fields are valid."}
                  </p>
                )}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

const FormField = ({ label, testid, type = "text", value, onChange, error }) => (
  <div>
    <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
      {label}
    </Label>
    <Input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 h-11 rounded-xl bg-brand-cream/40 border-black/10"
    />
    {error && <p className="mt-1 text-[11px] text-red-600">{error}</p>}
  </div>
);
